import { Component, Host, h, Prop, State, Event, EventEmitter, Watch, Element, Method } from '@stencil/core';
import type { ContactFormData, ContactFormEvent } from './types';
import { DISCIPLINE_OPTIONS } from './types';
import { updateFieldValidations, validateFullForm, contactFormSchema } from './validation';
import { sanitizeFormData, logContactFormEvent } from './utils';

@Component({
  tag: 'hd-contact-form',
  styleUrl: 'hd-contact-form.pcss',
  shadow: true,
})
export class HdContactForm {
  @Element() el: HTMLElement;

  @Prop({ mutable: true, reflect: true }) open = false;
  @Prop() embedded: boolean = false;
  @Prop() tooltip: boolean = false; // New prop for tooltip mode
  @Prop() triggerElement?: HTMLElement; // Element that triggers the tooltip
  @Prop() submitUrl?: string;
  @Prop() source?: string = 'contact_form';
  @Prop() prefilledEmail?: string = '';
  @Prop() prefilledName?: string = '';
  @Prop() formTitle?: string = 'Contact';
  @Prop() subtitle?: string;
  @Prop() termsAndConditions?: string = 'By sending this message you agree to the T&C.';
  @Prop() websiteLink?: string;
  @Prop() blurOverlay: boolean = true;
  @Prop() entityId: string = '';

  @State() formData: ContactFormData = {
    discipline: '',
    message: '',
    name: this.prefilledName || '',
    email: this.prefilledEmail || '',
    entityId: this.entityId
  };

  @State() errorFormFields: Record<keyof ContactFormData, boolean | null> = {
    discipline: null,
    message: null,
    name: null,
    email: null,
    entityId: null,
  };

  @State() isFormValid = false;
  @State() loading = false;
  @State() submitted = false;
  @State() submitError: string | null = null;
  @State() showSuccessMessage = false;
  @State() tooltipPosition = { top: 0, left: 0 };

  @Event() contactFormSubmit: EventEmitter<ContactFormEvent>;
  @Event() contactFormClose: EventEmitter<{ reason: string }>;

  private validationTimeouts: Record<string, number> = {};

  @Watch('prefilledEmail')
  watchPrefilledEmail(newValue: string) {
    this.formData = { ...this.formData, email: newValue };
  }

  @Watch('prefilledName')
  watchPrefilledName(newValue: string) {
    this.formData = { ...this.formData, name: newValue };
  }

  private isMobile(): boolean {
    return window.innerWidth < 768;
  }

  @Watch('open')
  watchOpen(newValue: boolean) {
    if (newValue && this.tooltip && this.triggerElement && !this.isMobile()) {
      this.calculateTooltipPosition();
    }
    // Handle mobile scroll lock
    if (this.isMobile()) {
      document.body.style.overflow = newValue ? 'hidden' : '';
    }
  }

  calculateTooltipPosition() {
    if (!this.triggerElement) return;

    if(this.isMobile()) {
      this.tooltipPosition = {
        top: 0,
        left: 0
      };
      return;
    }

    const formContainer = this.el.shadowRoot?.querySelector('.contact-container') as HTMLElement;
    if (!formContainer) return;

    const clientRect = this.triggerElement.getBoundingClientRect();

    const relativeLeft = Number(clientRect.left + window.scrollX);
    const relativeTop = Number(clientRect.top + window.scrollY);

    this.tooltipPosition = {
      top: relativeTop + this.triggerElement.offsetHeight + 7,
      left: relativeLeft
    };
  }

  componentWillLoad() {
    this.formData = {
      discipline: '',
      message: '',
      name: this.prefilledName || '',
      email: this.prefilledEmail || '',
      entityId: this.entityId
    };
  }

  componentDidLoad() {
    if (this.tooltip) {
      document.addEventListener('click', this.handleDocumentClick);
      window.addEventListener('resize', this.handleWindowResize);
    }
  }

  disconnectedCallback() {
    if (this.tooltip && !this.isMobile()) {
      document.removeEventListener('click', this.handleDocumentClick);
      window.removeEventListener('resize', this.handleWindowResize);
    }
  }

  private handleDocumentClick = (event: MouseEvent) => {
    if (!this.open || !this.tooltip || this.isMobile()) return;

    const target = event.target as HTMLElement;
    // Don't close if clicking inside the form or on the trigger element
    if (this.el.contains(target) ||
        (this.triggerElement && this.triggerElement.contains(target))) {
      return;
    }

    this.onClose(event, 'outside_click');
  };

  private handleWindowResize = () => {
    if (this.open && this.tooltip && this.triggerElement) {
      this.calculateTooltipPosition();
      this.open = false;
    }
  };

  updateField = (field: keyof ContactFormData, e: Event) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const value = target.value;

    this.formData = { ...this.formData, [field]: value };

    // Clear any existing timeout for this field
    if (this.validationTimeouts[field]) {
      clearTimeout(this.validationTimeouts[field]);
    }

    // Set new timeout for validation
    this.validationTimeouts[field] = window.setTimeout(() => {
      this.validateField(field, target);
    }, 500);
  };

  validateField = (field: keyof ContactFormData, target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
    const isValid = updateFieldValidations(field, target);
    this.errorFormFields = { ...this.errorFormFields, [field]: isValid };
    this.checkFormValidity();
  };

  checkFormValidity = () => {
    const errors = validateFullForm(this.formData);
    this.isFormValid = Object.keys(errors).length === 0 &&
                     this.formData.discipline !== '' &&
                     this.formData.message !== '' &&
                     this.formData.name !== '' &&
                     this.formData.email !== '';
  };

  onClose = (e: Event, reason: string) => {
    e.preventDefault();
    e.stopPropagation();
    this.open = false;
    this.contactFormClose.emit({ reason });
    logContactFormEvent('contact_form_closed', { reason });
  };

  handleSubmit = async (e: Event) => {
    e.preventDefault();

    if (this.loading || !this.isFormValid) return;

    this.loading = true;
    this.submitError = null;

    try {
      // Validate and sanitize form data
      const sanitizedData = sanitizeFormData(this.formData);
      const validationResult = contactFormSchema.safeParse(sanitizedData);

      if (!validationResult.success) {
        this.submitError = "Please check your form data and try again";
        this.loading = false;
        return;
      }

      // Emit event for parent component to handle
      this.contactFormSubmit.emit({
        formData: sanitizedData,
        source: this.source
      });

      // If submitUrl is provided, also send to server
      if (this.submitUrl) {
        const response = await fetch(this.submitUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...sanitizedData,
            source: this.source
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      this.submitted = true;

      logContactFormEvent('contact_form_submitted', {
        discipline: sanitizedData.discipline,
        source: this.source
      });

    } catch (error) {
      console.error('Contact form submission error:', error);
      this.submitError = "We couldn't send your message. Please try again later.";
      logContactFormEvent('contact_form_error', { error: error.message });
    } finally {
      this.loading = false;
    }
  };

  resetForm = () => {
    this.submitted = false;
    this.submitError = null;
    this.showSuccessMessage = false;
    this.formData = {
      discipline: '',
      message: '',
      name: this.prefilledName || '',
      email: this.prefilledEmail || '',
      entityId: this.entityId
    };
    this.errorFormFields = {
      discipline: null,
      message: null,
      name: null,
      email: null,
      entityId: null,
    };
    this.isFormValid = false;
  };

  @Method()
  async showSuccess() {
    this.showSuccessMessage = true;
  };

  @Method()
  async hideSuccess() {
    this.showSuccessMessage = false;
  };

  renderForm() {
    return (
      <form class="contact-form" onSubmit={this.handleSubmit}>
        <div class="form-header">
          <h2 class="title">{this.formTitle}</h2>
          {this.subtitle && <p class="subhead">{this.subtitle}</p>}
        </div>

        {/* Success Message - show when showSuccessMessage flag is true */}
        {this.showSuccessMessage && (
          <div class="success-message">
            <div class="success-icon">
              <hd-icon svg="checkCircle" width="24" height="24" stroke="var(--success-color)" fill="none"></hd-icon>
            </div>
            <div class="success-content">
              <h3>Sent</h3>
              <p>Contact request sent successfully!</p>
            </div>
          </div>
        )}

        {/* Submit Error - show when submitted and there is an error */}
        {this.submitted && this.submitError && (
          <div class="error-message">
            <div class="error-icon">
              <hd-icon svg="crossCircle" width="24" height="24" stroke="var(--error-color)" fill="none"></hd-icon>
            </div>
            <div class="error-content">
              <h3>Error</h3>
              <p>{this.submitError}</p>
            </div>
          </div>
        )}

        <div class={{'form-body': true, 'disabled': this.submitted}}>
          <div class="form-section">
            <p class="section-label">Your Discipline</p>
            <div class="discipline-options">
              {DISCIPLINE_OPTIONS.map((option) => (
                <button
                  type="button"
                  class={{
                    'discipline-option': true,
                    'selected': this.formData.discipline === option
                  }}
                  onClick={() => {
                    this.formData = { ...this.formData, discipline: option };
                    this.checkFormValidity();
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
            {this.errorFormFields.discipline === false && (
              <p class="error-message">Please select your discipline</p>
            )}
          </div>

          <div class="form-group">
            <label htmlFor="message">Message (English recommended)</label>
            <textarea
              id="message"
              value={this.formData.message}
              placeholder="Hello, I'm interested to know..."
              onInput={(e) => this.updateField('message', e)}
              onBlur={(e) => this.validateField('message', e.target as HTMLTextAreaElement)}
              rows={4}
            />
            <p class="error-message"></p>
          </div>

          <div class="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              value={this.formData.name}
              placeholder="Your name"
              onInput={(e) => this.updateField('name', e)}
              onBlur={(e) => this.validateField('name', e.target as HTMLInputElement)}
            />
            <p class="error-message"></p>
          </div>

          <div class="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={this.formData.email}
              placeholder="Your email"
              onInput={(e) => this.updateField('email', e)}
              onBlur={(e) => this.validateField('email', e.target as HTMLInputElement)}
            />
            <p class="error-message"></p>
          </div>
        </div>

        <div class="form-footer">
          <button
            type="submit"
            class="submit-button"
            disabled={!this.isFormValid || this.loading}
          >
            {this.loading ? 'SENDING...' : 'SEND MESSAGE'}
          </button>

          <p class="disclaimer" innerHTML={this.termsAndConditions}></p>

          {this.websiteLink && (
            <div class="alternative-contact">
              <div class="divider">
                <span>OR</span>
              </div>
              <a href={this.websiteLink} target="_blank" class="website-link">
                <hd-icon svg="web" width="16" height="16"></hd-icon>
                Visit Website
              </a>
            </div>
          )}
        </div>

        <slot name="recaptcha"></slot>
      </form>
    );
  }

  render() {
    const isTooltipMode = this.tooltip && !this.isMobile();

    const hostClasses = {
      'open': this.open,
      'embedded': this.embedded,
      'tooltip': isTooltipMode,
    };

    const containerStyle = isTooltipMode && this.open && !this.isMobile() ? {
      position: 'absolute',
      top: `${this.tooltipPosition.top}px`,
      left: `${this.tooltipPosition.left}px`,
    } : {};

    return (
      <Host
        class={hostClasses}
        aria-hidden={!this.open}
      >
        {!this.embedded && !isTooltipMode && (
          <div
            class={{
              'backdrop-overlay': true,
              'no-blur': !this.blurOverlay
            }}
            onClick={(e) => this.onClose(e, "overlay_click")}
          ></div>
        )}

        <div class="contact-container" style={containerStyle}>
          {!this.embedded && (
            <button class="close-button" onClick={(e) => this.onClose(e, "close_button")}>
              <hd-icon svg="cancel" width="12" height="12"></hd-icon>
            </button>
          )}

          {this.renderForm()}
        </div>
      </Host>
    );
  }
}